"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { ChevronRight, Lock, type LucideIcon } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FadeLabel } from "./parts";

export type NavChild = { href: string; label: string; icon: LucideIcon; active: boolean; locked?: boolean };

/**
 * A sidebar area with sub-pages. The name always goes to the area's main page; its other pages open in a
 * floating panel next to the sidebar (on hover or with the arrow), the same when the sidebar is collapsed,
 * so the sidebar never changes height.
 */
export function SidebarNavGroup({
  headerHref,
  label,
  icon: Icon,
  expanded,
  headerActive,
  children,
}: {
  headerHref: string;
  label: string;
  icon: LucideIcon;
  expanded: boolean;
  headerActive: boolean;
  children: NavChild[];
}) {
  const pathname = usePathname();
  const rowRef = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [openedAt, setOpenedAt] = useState<string | null>(null);
  const panelId = useId();
  // Navigating anywhere closes the panel.
  const open = pos !== null && openedAt === pathname;

  const show = () => {
    clearTimeout(timer.current);
    const r = rowRef.current?.getBoundingClientRect();
    if (!r) return;
    setPos({ top: Math.min(r.top, window.innerHeight - 48 - children.length * 40), left: r.right + 8 });
    setOpenedAt(pathname);
  };
  const hideSoon = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setPos(null), 180);
  };
  const hoverOpen = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(show, 120);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setPos(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  useEffect(() => () => clearTimeout(timer.current), []);

  return (
    <div ref={rowRef} onMouseEnter={hoverOpen} onMouseLeave={hideSoon}>
      <div
        className={`group flex w-full items-center rounded-xl text-sm transition-colors ${
          headerActive ? "bg-accent/10 font-semibold text-accent dark:bg-neutral-800" : "font-medium text-fg-soft hover:bg-surface-muted hover:text-fg"
        } ${expanded ? "" : "h-10 w-10 justify-center"}`}
      >
        <Link href={headerHref} aria-label={expanded ? undefined : label} className={`flex min-w-0 flex-1 items-center gap-3 ${expanded ? "py-2.5 pl-3" : "justify-center"}`}>
          <Icon size={18} className={`shrink-0 transition-colors ${headerActive ? "text-accent" : "text-muted group-hover:text-accent"}`} />
          {expanded && (
            <span className="min-w-0 flex-1 text-left">
              <FadeLabel expanded={expanded}>{label}</FadeLabel>
            </span>
          )}
        </Link>
        {expanded && (
          <div className="flex shrink-0 items-center gap-0.5 pr-1">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (open) setPos(null);
                else show();
              }}
              aria-expanded={open}
              aria-controls={panelId}
              aria-label={`More ${label} pages`}
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors ${headerActive ? "text-accent hover:bg-accent/20" : "text-subtle hover:bg-surface-strong/60 hover:text-fg-soft"}`}
            >
              <ChevronRight size={15} className="shrink-0" />
            </button>
          </div>
        )}
      </div>

      {open &&
        createPortal(
          <div
            id={panelId}
            role="menu"
            aria-label={label}
            onMouseEnter={() => clearTimeout(timer.current)}
            onMouseLeave={hideSoon}
            style={{ top: pos.top, left: pos.left }}
            className="fixed z-overlay w-60 rounded-xl border border-line bg-surface p-1.5 shadow-xl"
          >
            <p className="px-2.5 pb-1 pt-1.5 text-xs font-semibold text-muted">{label}</p>
            {children.map((child) => {
              const ChildIcon = child.icon;
              return (
                <Link
                  key={child.href}
                  role="menuitem"
                  href={child.href}
                  onClick={() => setPos(null)}
                  className={`flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-colors ${
                    child.active ? "bg-accent/10 font-semibold text-accent dark:bg-neutral-800" : "font-medium text-fg-soft hover:bg-surface-muted hover:text-fg"
                  }`}
                >
                  <ChildIcon size={15} className="shrink-0" />
                  {child.label}
                  {child.locked && <Lock size={13} className="ml-auto shrink-0 opacity-50" />}
                </Link>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
}
